/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { colors } from "@farmlands/mui-theme";
import { Stack, useMediaQuery, useTheme } from "@mui/material";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { Button } from "../../../../components/Button";
import { Loader } from "../../../../components/Loader";
import { ModalActions } from "../../../../components/Modal";
import Typography from "../../../../components/Typography";
import { formatNumber } from "../../../../util/helpers";
import { selectCurrentFarmerAccount } from "../../../azure-b2c/storage";

import { getDrawdownConfig } from "./api";
import { useDrawdownRequestContext } from "./DrawdownRequestContext";
import ProductLine from "./ProductLine";
import { ProductRequest, RequestMonth } from "./types";

interface ProductRequestValues {
  requestQuantity: string;
  feedRate?: string;
  additives?: string;
}

const Step1 = () => {
  const { salesAgreement, setState, state } = useDrawdownRequestContext();
  const isSm = useMediaQuery(useTheme().breakpoints.down("sm"));
  const [selectedMonth, setSelectedMonth] = useState<RequestMonth | null>(state.selectedMonth);
  const [months, setMonths] = useState<RequestMonth[]>([]);
  const currentMonth = dayjs();
  const agreementStartMonth = dayjs(salesAgreement?.startDate);
  const agreementEndMonth = dayjs(salesAgreement?.endDate);
  const account = useSelector(selectCurrentFarmerAccount);

  useEffect(() => {
    if (account && salesAgreement && !state.drawdownConfig) {
      getDrawdownConfig(salesAgreement?.id, account?.accountNumber).then((config) => {
        setState({ ...state, drawdownConfig: config.data });
      });
    }
  }, [account, salesAgreement]);

  useEffect(() => {
    // only allow user request current month only after the agreement end date
    const numberOfMonthsForRequest = currentMonth.isAfter(agreementEndMonth) ? 1 : 2;

    const months: RequestMonth[] = Array.from({ length: numberOfMonthsForRequest }, (_, index) => {
      const month = currentMonth.isBefore(agreementStartMonth)
        ? agreementStartMonth.add(index, "month")
        : currentMonth.add(index, "month");
      return {
        label: month.format("MMMM YYYY"),
        value: month.toDate(),
      };
    }).filter((month) => dayjs(month.value).isBefore(agreementEndMonth));

    setMonths(months);
    if (state.selectedMonth) {
      setSelectedMonth(state.selectedMonth);
    } else {
      setSelectedMonth(months[0]);
    }
  }, [state.selectedMonth, salesAgreement]);

  const outOfRange = (startDate: dayjs.Dayjs, endDate: dayjs.Dayjs, date: dayjs.Dayjs) => {
    return dayjs(date).isBefore(dayjs(startDate)) || dayjs(date).isAfter(dayjs(endDate));
  };

  useEffect(() => {
    if (!salesAgreement) {
      return;
    }
    const products = salesAgreement?.rawLines.filter((line) => line.scheduledDate === null) ?? [];

    const _productsThisMonth: ProductRequest[] = products
      .map((product) => {
        const variantNumber = product.productVariantNumber;

        const productForMonth = salesAgreement?.rawLines.filter((line) => {
          const outRange = outOfRange(
            dayjs(line?.startDate),
            dayjs(line?.endDate),
            dayjs(selectedMonth?.value)
          );

          return (
            !outRange &&
            line.productVariantNumber === variantNumber &&
            dayjs(line.scheduledDate).month() === dayjs(selectedMonth?.value).month() &&
            dayjs(line.scheduledDate).year() === dayjs(selectedMonth?.value).year()
          );
        });

        const match = state.productRequest.find(
          (reqProduct) => reqProduct.productVariantNumber === variantNumber
        );

        const initialQuantity = {
          committed: 0,
          delivered: 0,
          invoiced: 0,
          remaining: 0,
          released: 0,
        };

        const max = {
          max: 0,
          maxLeft: 0,
          maxWithTolerance: 0,
        };

        const quantity =
          productForMonth?.reduce((acc, line) => {
            max.maxLeft += line.quantity.remaining;

            return {
              committed: acc.committed + line.quantity.committed,
              delivered: acc.delivered + line.quantity.delivered,
              invoiced: acc.invoiced + line.quantity.invoiced,
              remaining: acc.remaining + line.quantity.remaining,
              released: acc.released + line.quantity.released,
            };
          }, initialQuantity) ?? initialQuantity;

        // .lines are aggregated, so there is only 1 product with all the tally already.
        const totalProduct = salesAgreement?.lines.find(
          (line) => line.productVariantNumber === variantNumber
        );

        // Use rawLines to find the parent line with scheduledDate === null
        const parentLine = salesAgreement?.rawLines.find(
          (line) => line.productVariantNumber === variantNumber && line.scheduledDate === null
        );

        const tolerancePct = Number(parentLine?.tolerancePercentage ?? 0);

        if (totalProduct) {
          const consumed =
            totalProduct.quantity.delivered +
            totalProduct.quantity.invoiced +
            totalProduct.quantity.released;
          max.max = totalProduct.quantity.committed;
          max.maxLeft = max.max - consumed;
          // Mirror backend formula: remaining + ceil(committed × pct / 100)
          max.maxWithTolerance = max.maxLeft + Math.ceil((max.max * tolerancePct) / 100);
        }

        return {
          ...product,
          requestQuantity: match?.requestQuantity ?? 0,
          feedRate: match?.feedRate,
          additives: match?.additives,
          quantity,
          max: max.max,
          maxLeft: max.maxLeft,
          maxWithTolerance: max.maxWithTolerance,
        };
      })
      .filter(
        (product) =>
          !outOfRange(dayjs(product.startDate), dayjs(product.endDate), dayjs(selectedMonth?.value))
      );

    if (state.drawdownConfig?.additives) {
      // Create a set from additives by itemNumber;
      // set products.additives to the set if it matches itemNumber
      const additives = state.drawdownConfig?.additives;
      _productsThisMonth.forEach((product) => {
        const itemNumber = product.productVariantNumber.split(":")[0].trim();
        const additive = additives.find((additive) => additive.itemNumber === itemNumber);

        if (additive) {
          product.additives = additive.additives;
          product.feedRate = additive.feedRate;
        }
      });
    }
    // sort((a) => a.maxWithTolerance - a.maxLeft >= a.maxWithTolerance ? 1 : -1,)
    setProductsThisMonth(_productsThisMonth);

    const initialAmounts: Record<string, ProductRequestValues> = {};
    _productsThisMonth.forEach((product) => {
      initialAmounts[product.productVariantNumber] = {
        requestQuantity: product.requestQuantity.toString(),
        feedRate: product.feedRate,
        additives: product.additives,
      };
    });

    setAmounts(initialAmounts);
  }, [state.drawdownConfig, selectedMonth, salesAgreement]);

  const [productsThisMonth, setProductsThisMonth] = useState<ProductRequest[]>([]);
  const [amounts, setAmounts] = useState<Record<string, ProductRequestValues>>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [invalidAmount, setInvalidAmount] = useState<boolean>(false);

  const handleAmountChange = (
    product: ProductRequest,
    updatedValues: Partial<ProductRequestValues>
  ) => {
    setAmounts((prev) => {
      // Perform validation based on the updated values
      const newRequestQuantity = formatNumber(
        Number(
          typeof updatedValues.requestQuantity === "undefined" ? 0 : updatedValues.requestQuantity
        )
      );
      const remainingAmount = formatNumber(product.maxWithTolerance);

      setErrorMessage(null);
      if (newRequestQuantity > remainingAmount) {
        setInvalidAmount(true);
      } else {
        setInvalidAmount(false);
      }

      return {
        ...prev,
        [product.productVariantNumber]: {
          ...prev[product.productVariantNumber],
          ...updatedValues,
        },
      };
    });
  };

  const handleNextStep = () => {
    const productsWithAmounts = productsThisMonth.filter(
      (product) =>
        amounts[product.productVariantNumber] &&
        Number(amounts[product.productVariantNumber].requestQuantity) > 0
    );
    const totalAmount = Object.values(amounts).reduce(
      (sum, { requestQuantity }) => sum + Number(requestQuantity),
      0
    );

    if (productsWithAmounts.length === 0 || totalAmount === 0) {
      setErrorMessage("Please enter the requested amount for at least one product.");
      return;
    }

    if (invalidAmount) {
      setErrorMessage("Please enter valid amounts for all products.");
      return;
    }

    setErrorMessage(null);
    const productRequest = productsWithAmounts.map((product) => ({
      ...product,
      requestQuantity: Number(amounts[product.productVariantNumber]?.requestQuantity),
      feedRate: amounts[product.productVariantNumber]?.feedRate,
      additives: amounts[product.productVariantNumber]?.additives,
      max: product.max,
      maxLeft: product.maxLeft,
      maxWithTolerance: product.maxWithTolerance,
    }));

    setState({ productRequest, tabIndex: state.tabIndex + 1, selectedMonth });
  };

  if (!state.drawdownConfig) {
    return (
      <Stack
        direction="column"
        width="100%"
        height="100%"
        justifyContent="center"
        alignItems="center"
      >
        <Loader title="Starting order" />
      </Stack>
    );
  }

  return (
    <Stack direction="column" gap={3}>
      <Stack direction="column">
        <Typography variant="body2" fontWeight={"bold"} color="primary600">
          Set delivery amount
        </Typography>

        <Typography variant="caption" color="grey900">
          How much you would like to receive this month?
        </Typography>
      </Stack>

      <Stack direction="row" spacing={2}>
        {months.map((month) => {
          const isCurruntMonth = selectedMonth?.label === month.label;
          return (
            <Button
              key={month.value.getTime()}
              variant="contained"
              onClick={() => setSelectedMonth(month)}
              sx={{
                backgroundColor: isCurruntMonth ? colors.primary200 : colors.primary100,
                color: colors.primary600,
                fontWeight: isCurruntMonth ? 700 : 400,
                "&:hover": {
                  backgroundColor: colors.primary100,
                },
              }}
            >
              {month.label}
            </Button>
          );
        })}
      </Stack>

      <Stack spacing={3}>
        {productsThisMonth.map((product) => (
          <ProductLine
            month={dayjs(selectedMonth?.value).format("MMMM")}
            showAdditives={state.drawdownConfig?.showAdditives}
            product={product}
            key={product.productVariantNumber}
            amounts={amounts}
            handleAmountChange={handleAmountChange}
          />
        ))}
      </Stack>

      <ModalActions showDivider>
        <Stack
          direction={isSm ? "column" : "row"}
          alignItems="center"
          justifyContent="space-between"
          flex={1}
        >
          <Typography variant="body2" color="error500">
            {errorMessage}
          </Typography>

          <Button
            key="next-step-btn"
            variant="contained"
            onClick={handleNextStep}
            sx={(theme) => ({
              [theme.breakpoints.down("sm")]: {
                width: "100%",
              },
            })}
          >
            Next
          </Button>
        </Stack>
      </ModalActions>
    </Stack>
  );
};

export default Step1;
